<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import type { ILiveRowLines } from '@/domain/menu-import/live-rows';
  import { formatMoney } from '@/domain/store-setup/money';

  /**
   * The quiet lines under a dish already on the menu (plan §3.5 «Sub-lines
   * of linked dishes»): whether it stays hidden («Oculto en su menú» with
   * «Mostrarlo»), shows again, or gets hidden, and «No vimos el precio en la
   * foto: queda {price}».
   */
  const props = defineProps<{
    lines: ILiveRowLines;
    currency: string;
    country: string;
  }>();
  const emit = defineEmits<{
    /** «Mostrarlo». */
    (e: 'show'): void;
  }>();

  const BADGE_KEYS = {
    stillHidden: 'menuImport.update.hiddenInMenu',
    willShow: 'menuImport.update.willShow',
    willHide: 'menuImport.item.hidden',
  } as const;

  const badgeKey = computed(() =>
    props.lines.visibility === null ? null : BADGE_KEYS[props.lines.visibility]
  );
  const noPhotoPrice = computed(() =>
    props.lines.noPhotoPrice === null
      ? null
      : formatMoney(props.lines.noPhotoPrice, props.currency, props.country)
  );
</script>

<template>
  <div
    v-if="badgeKey !== null || noPhotoPrice !== null"
    class="v2-menu-live-row-lines"
  >
    <p v-if="badgeKey !== null" class="line">
      <PosUiBadge
        size="sm"
        :color="lines.visibility === 'willShow' ? 'info' : 'neutral'"
        >{{ $t(badgeKey) }}</PosUiBadge
      >
      <span v-if="lines.visibility === 'stillHidden'" class="line-text">{{
        $t('menuImport.update.stillHidden')
      }}</span>
      <button
        v-if="lines.canShow"
        type="button"
        class="line-link"
        @click="emit('show')"
      >
        {{ $t('menuImport.update.show') }}
      </button>
    </p>
    <p v-if="noPhotoPrice !== null" class="line line-text">
      {{ $t('menuImport.update.noPhotoPrice', { price: noPhotoPrice }) }}
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-live-row-lines {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-1;

    .line {
      @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-[13px] tw-leading-5;
    }
    .line-text {
      @apply tw-text-neutral-600;
    }
    .line-link {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4 md:tw-min-h-0;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
